import {
  Avatar,
  Button,
  Fade,
  Grid,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import MyAvailabilityCalendar from 'src/components/MyAvailability/my-availability-calendar';
import { MyRequest } from './my-request';
import { ExpandLess, KeyboardArrowDown, Search } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { getListOfTherapistAvailability } from 'src/redux/actions/myAvailabilityAction';
import { useSettings } from 'src/hooks/use-settings';
import useCurrentUser from 'src/hooks/use-current-user';

function MyAvailability() {
  const dispatch = useDispatch();
  const settings = useSettings();
  const inputRef = useRef<HTMLInputElement>(null);
  const getMyAvailability = useSelector((state: any) => state.getMyAvailabilityReducer);
  const listOfTherapistAvailability = useSelector(
    (state: any) => state.getListOfTherapistAvaiReducer
  );
  const enablePto = useSelector(
    (state: any) => state?.thePayComeDetailsReducer?.response?.data?.enablePto
  );

  const [providerId] = useCurrentUser(['providerId']);
  const userRole = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentRoles
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTherapist, setSelectedTherapist] = useState<any>('');
  const [showEditAvailability, setShowEditAvailability] = useState(false);
  const open = Boolean(anchorEl);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = listOfTherapistAvailability?.response?.data
    ?.slice()
    .sort((a: any, b: any) => a?.lastName?.localeCompare(b?.lastName))
    .filter((therapist: any) => {
      const normalizedLabel = (therapist?.lastName + therapist?.firstName)
        ?.replace(/[\s,]/g, '')
        ?.toLowerCase();
      const normalizedSearchTerm = searchTerm?.replace(/[\s,]/g, '')?.toLowerCase();

      return normalizedLabel?.includes(normalizedSearchTerm);
    });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    dispatch(getListOfTherapistAvailability());
  }, []);

  useEffect(() => {
    setShowEditAvailability(enablePto);
  }, [enablePto]);

  const handleSelectedTherapist = (therapist: any) => {
    setSelectedTherapist(therapist);
    setAnchorEl(null);
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
      }}
    >
      <Container
        sx={{ padding: '0px 0px !important' }}
        maxWidth={settings.stretch ? false : 'xl'}
      >
        <Grid
          sx={{
            flexGrow: 1,
            width: '100%',
          }}
        >
          <Grid
            container
            mt={3}
            justifyContent={'space-between'}
          >
            <Box
              display={'flex'}
              sx={{ alignItems: 'center' }}
            >
              <Typography
                variant="h4"
                sx={{ marginLeft: '24px' }}
              >
                {!selectedTherapist || providerId === selectedTherapist?.uuid
                  ? 'My Availability'
                  : 'Availability'}
              </Typography>
              {userRole?.includes('dor') ? (
                <Button
                  id="basic-button"
                  onClick={handleClick}
                  sx={{
                    ml: 2,
                    borderColor: '#afb3ba9e',
                    color: '#111927',
                    width: 230,
                    pr: 1.5,
                    pl: 2,
                    height: '40px',
                    display: 'flex',
                    justifyContent: 'start',
                    '&:hover': {
                      color: '#111927',
                      borderColor: '#afb3ba9e',
                    },
                  }}
                  variant="outlined"
                  startIcon={
                    <Avatar
                      src={selectedTherapist?.avatar}
                      sx={{ width: 28, height: 28 }}
                    />
                  }
                  endIcon={open ? <ExpandLess /> : <KeyboardArrowDown />}
                  title={
                    selectedTherapist
                      ? `${selectedTherapist?.lastName} ${selectedTherapist?.firstName}`
                      : ''
                  }
                >
                  <Typography
                    sx={{
                      width: 200,
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textAlign: 'left',
                    }}
                  >
                    {selectedTherapist?.uuid
                      ? `${selectedTherapist?.lastName} ${selectedTherapist?.firstName}`
                      : 'My Schedule'}
                  </Typography>
                </Button>
              ) : null}
            </Box>

            <Box
              sx={{
                background: '#EBEEFE',
                height: '24px',
                borderRadius: '12px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginLeft: '24px',
              }}
              p={1.8}
              mr={5}
            >
              <Typography
                sx={{ fontSize: '14px', marginTop: '3px' }}
                variant="subtitle2"
              >{`${getMyAvailability?.data?.t2} Available hours this week`}</Typography>
            </Box>
          </Grid>
          <Grid container>
            <Grid
              item
              xs={12}
              xl={
                !showEditAvailability &&
                (!selectedTherapist || providerId === selectedTherapist?.uuid)
                  ? 9
                  : 0
              }
            >
              <MyAvailabilityCalendar selectedTherapist={selectedTherapist} />
            </Grid>
            {!showEditAvailability &&
            (!selectedTherapist || providerId === selectedTherapist?.uuid) ? (
              <Grid
                item
                xs={12}
                xl={3}
                mt={9}
                p={2}
                sx={{
                  overflow: 'scroll',
                  height: '1000px',
                }}
              >
                <MyRequest />
              </Grid>
            ) : null}
          </Grid>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            sx={{
              width: '260px',
            }}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
          >
            <TextField
              variant="outlined"
              placeholder={`Search by therapist`}
              onChange={(event: any) => {
                setSearchTerm(event.target.value);
              }}
              value={searchTerm}
              sx={{
                p: '0px 5px',
                '& .MuiInputBase-input': {
                  padding: '12px 0px',
                },
              }}
              inputRef={inputRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <Grid
              height={400}
              sx={{ overflowY: 'scroll' }}
            >
              <Box sx={{ width: '220px' }}>
                {filteredOptions?.length ? (
                  filteredOptions?.map((therapist: any) => (
                    <MenuItem
                      key={therapist?.uuid}
                      onClick={() => handleSelectedTherapist(therapist)}
                      sx={{ display: 'flex', py: 1 }}
                    >
                      <Avatar
                        src={therapist?.avatar}
                        sx={{ width: 30, height: 30 }}
                      />
                      <Tooltip
                        title={
                          therapist?.name?.length > 15
                            ? `${therapist?.lastName} ${therapist?.firstName}`
                            : ''
                        }
                      >
                        <Typography
                          sx={{
                            ml: 1,
                            overflow: 'hidden',
                            textAlign: 'left',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {`${therapist?.lastName} ${therapist?.firstName}`}
                        </Typography>
                      </Tooltip>
                    </MenuItem>
                  ))
                ) : (
                  <Typography
                    color={'text.secondary'}
                    variant="subtitle2"
                    sx={{
                      textAlign: 'center',
                      width: '220px',
                      p: 3,
                    }}
                  >
                    No results found
                  </Typography>
                )}
              </Box>
            </Grid>
          </Menu>
        </Grid>
      </Container>
    </Box>
  );
}

export default MyAvailability;
